import { Dispatch } from 'redux'

import { useThunkActions } from 'redux/actions/useThunkActions'
import {
  copy,
  create,
  createWidget,
  destroy,
  getAll,
  putFilters,
  reorderBlocks,
  update,
  visit
} from 'redux/api/Reports'
import { reorderBlockWidgets } from 'redux/api/ReportBlocks'

import { TPutResourceFilter } from 'types/GlobalDashboardFilter'
import { EPublicationStatus } from 'types/Enums'

import * as actions from './actions'
import { TCreateReportWidgetBody, TUpdateReportBody } from 'types/GlobalReport'
import { toggleSnackbarOpen } from '../Snackbar'
import { SnackbarMessage } from 'types/GlobalSnackbar'

type NavigateCallbackFunction = (id: string) => void

export function tryGetAllReports() {
  return (dispatch: Dispatch) => {
    dispatch(actions.getAll())

    getAll()
      .then((response) => {
        dispatch(actions.getAllSuccess(response.data.data))
      })
      .catch(() => {
        dispatch(actions.getAllFailed())
      })
  }
}

function tryCreateReport(
  body: { folder_id: string | null },
  navigate: NavigateCallbackFunction
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.create())

    create(body)
      .then((response) => {
        dispatch(actions.createSuccess(response.data.data))
        navigate(response.data.data.id)
      })
      .catch(() => {
        dispatch(actions.createFailed())
      })
  }
}

function tryDeleteReport(id: string, navigate: () => void) {
  return (dispatch: Dispatch) => {
    dispatch(actions.destroy())

    destroy(id)
      .then(() => {
        dispatch(actions.destroySuccess(id))
        navigate()
      })
      .catch(() => {
        dispatch(actions.destroyFailed())
      })
  }
}

function tryUpdateReport(
  id: string,
  body: TUpdateReportBody,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.update())

    update(id, body)
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.updateFailed())

        callback?.(true)
      })
  }
}

function tryToggleReportStatus(id: string, currentStatus: string) {
  return (dispatch: Dispatch) => {
    const isPublished = currentStatus === EPublicationStatus.PUBLISHED
    const nextStatus = isPublished
      ? EPublicationStatus.DRAFT
      : EPublicationStatus.PUBLISHED

    dispatch(actions.update())

    update(id, { status: nextStatus })
      .then((response) => {
        dispatch(actions.updateSuccess(response.data.data))
        dispatch(
          toggleSnackbarOpen(
            isPublished
              ? SnackbarMessage.ReportUnpublished
              : SnackbarMessage.ReportPublished
          )
        )
      })
      .catch(() => {
        dispatch(actions.updateFailed())
        dispatch(
          toggleSnackbarOpen(
            isPublished
              ? SnackbarMessage.ReportUnpublishedFailed
              : SnackbarMessage.ReportPublishedFailed
          )
        )
      })
  }
}

function tryReorderBlocks(reportId: string, orderedIds: string[]) {
  return (dispatch: Dispatch) => {
    dispatch(actions.reorderBlocks())

    reorderBlocks(
      reportId,
      orderedIds.map((id, index) => ({ id, index }))
    )
      .then(() => {
        dispatch(actions.reorderBlocksSuccess({ reportId, order: orderedIds }))
      })
      .catch(() => {
        dispatch(actions.reorderBlocksFailed())
      })
  }
}

// TODO: move to ReportWidget actions
function tryCreateReportWidget(
  reportId: string,
  body: TCreateReportWidgetBody,
  callback?: (err?: boolean) => void
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.createWidget())

    createWidget(reportId, body)
      .then((response) => {
        const { block, widget } = response.data.data

        dispatch(actions.createWidgetSuccess({ block, widget }))

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetCreated))

        callback?.()
      })
      .catch(() => {
        dispatch(actions.createWidgetFailed())

        dispatch(toggleSnackbarOpen(SnackbarMessage.WidgetCreatedFailed))

        callback?.(true)
      })
  }
}

function tryCopyReport(id: string, navigate: NavigateCallbackFunction) {
  return (dispatch: Dispatch) => {
    dispatch(actions.copyReport())

    copy(id)
      .then((response) => {
        dispatch(actions.copyReportSuccess(response.data.data))
        dispatch(toggleSnackbarOpen(SnackbarMessage.ReportDuplicated))
        navigate(response.data.data.id)
      })
      .catch(() => {
        dispatch(actions.copyReportFailed())
        dispatch(toggleSnackbarOpen(SnackbarMessage.ReportDuplicatedFailed))
      })
  }
}

function tryPutReportFilters(reportId: string, data: TPutResourceFilter[]) {
  return (dispatch: Dispatch) => {
    dispatch(actions.putFilters())

    putFilters(reportId, data)
      .then((response) =>
        dispatch(
          actions.putFiltersSuccess({ reportId, filters: response.data.data })
        )
      )
      .catch(() => dispatch(actions.putFiltersFailed()))
  }
}

export function tryVisitReport(id: string) {
  return (dispatch: Dispatch) => {
    dispatch(actions.visitReport(id))

    visit(id)
      .then(() => {
        dispatch(actions.visitReportSuccess(id))
      })
      .catch(() => {
        dispatch(actions.visitReportFailed())
      })
  }
}

function tryReorderBlockWidgets(
  reportId: string,
  blockId: string,
  orderedIds: string[]
) {
  return (dispatch: Dispatch) => {
    dispatch(actions.reorderBlockWidgets())

    reorderBlockWidgets(
      blockId,
      orderedIds.map((id, index) => ({ id, index }))
    )
      .then(() => {
        dispatch(
          actions.reorderBlockWidgetsSuccess({
            reportId,
            blockId,
            order: orderedIds
          })
        )
      })
      .catch(() => {
        dispatch(actions.reorderBlockWidgetsFailed())
      })
  }
}

export const useReportActions = () => {
  return {
    tryCopyReport: useThunkActions(tryCopyReport),
    tryCreateReportWidget: useThunkActions(tryCreateReportWidget),
    tryCreateReport: useThunkActions(tryCreateReport),
    tryDeleteReport: useThunkActions(tryDeleteReport),
    tryGetAllReports: useThunkActions(tryGetAllReports),
    tryPutReportFilters: useThunkActions(tryPutReportFilters),
    tryReorderBlocks: useThunkActions(tryReorderBlocks),
    tryReorderBlockWidgets: useThunkActions(tryReorderBlockWidgets),
    tryToggleReportStatus: useThunkActions(tryToggleReportStatus),
    tryUpdateReport: useThunkActions(tryUpdateReport),
    tryVisitReport: useThunkActions(tryVisitReport)
  }
}
